/**
 * @voltxampere/engine — Assertion DSL evaluator (Sprint 9)
 *
 * Takes a parsed {@link AssertionAST} + a {@link SimulationResult}
 * snapshot and produces an {@link AssertionResult} describing
 * pass/fail/skip + the actual/expected values in human-readable
 * engineering units.
 *
 * Responsibilities kept deliberately narrow:
 *
 *   - resolve node / component references against the snapshot,
 *   - dispatch to the appropriate comparison kind (binary,
 *     tolerance, oscillation-placeholder, peak-shorthand),
 *   - render the i18n'd message using
 *     {@link formatEngineering} so `5.0030` becomes `5.00 V`,
 *   - produce a "did you mean" suggestion via Levenshtein when
 *     a reference misses.
 *
 * Explicitly out of scope for Sprint 9:
 *
 *   - full FFT-based oscillation check (Sprint 13+),
 *   - AC-result evaluation (Sprint 12+),
 *   - `allTimes` sample-by-sample diagnostics — Sprint 9 treats
 *     allTimes as "check peak/rms across the waveform" rather
 *     than inspecting every sample.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { suggestClosest } from "@voltxampere/shared";
import { BUILTIN_NAMES, computeBuiltinDC, computeBuiltinTransient, isBuiltinName, } from "./measurements.js";
import { formatEngineering, unitBase } from "./units.js";
/** Entry point. */
export function evaluateAssertion(ast, snapshot, currentAnalysis, context) {
    if (ast.condition && !conditionMatches(ast.condition, currentAnalysis)) {
        return skippedResult(ast, currentAnalysis, context);
    }
    try {
        switch (ast.kind) {
            case "binary":
                return evaluateBinary(ast, snapshot, currentAnalysis, context);
            case "tolerance":
                return evaluateTolerance(ast, snapshot, currentAnalysis, context);
            case "oscillation":
                return evaluateOscillationPlaceholder(ast, currentAnalysis, context);
            case "peak":
                return evaluatePeak(ast, snapshot, currentAnalysis, context);
        }
    }
    catch (err) {
        if (err instanceof ResolutionError) {
            return resolutionErrorResult(ast, err, currentAnalysis, context);
        }
        throw err;
    }
}
// -------------------------------------------------------------
// Resolution
// -------------------------------------------------------------
class ResolutionError extends Error {
    target;
    kind;
    constructor(message, target, kind) {
        super(message);
        this.target = target;
        this.kind = kind;
        this.name = "ResolutionError";
    }
}
/** Map a user-facing net name to the snapshot's key. */
function resolveNetKey(net, ctx) {
    return ctx.nodeNameToId?.get(net) ?? net;
}
function resolveExpr(expr, ctx) {
    switch (expr.kind) {
        case "literal":
            return expr.unit !== undefined
                ? { value: expr.value, unit: expr.unit }
                : { value: expr.value };
        case "nodeRef":
            return { value: lookupNodeVoltage(expr.net, ctx), unit: "V" };
        case "componentRef":
            return { value: lookupComponentProperty(expr.id, expr.property, ctx), unit: "A" };
        case "binary": {
            const l = resolveExpr(expr.left, ctx);
            const r = resolveExpr(expr.right, ctx);
            switch (expr.op) {
                case "+":
                    return { value: l.value + r.value, ...(l.unit ? { unit: l.unit } : {}) };
                case "-":
                    return { value: l.value - r.value, ...(l.unit ? { unit: l.unit } : {}) };
                case "*":
                    return { value: l.value * r.value };
                case "/":
                    return { value: l.value / r.value };
            }
            break;
        }
        case "function":
            return resolveFunction(expr.name, expr.args, ctx);
    }
    throw new Error("unreachable resolver branch");
}
function lookupNodeVoltage(net, ctx) {
    const key = resolveNetKey(net, ctx);
    if (ctx.currentAnalysis === "DC") {
        const v = ctx.snapshot.dc?.nodeVoltages.get(key);
        if (v === undefined)
            throw new ResolutionError(`node not found: ${net}`, net, "node");
        return v;
    }
    if (ctx.currentAnalysis === "TRANSIENT") {
        const ts = ctx.snapshot.transient?.timeSteps;
        if (!ts || ts.length === 0) {
            throw new ResolutionError(`node not found: ${net}`, net, "node");
        }
        const last = ts[ts.length - 1];
        const v = last?.nodeVoltages.get(key);
        if (v === undefined)
            throw new ResolutionError(`node not found: ${net}`, net, "node");
        return v;
    }
    // AC evaluation not wired in Sprint 9.
    throw new ResolutionError(`AC node lookup not implemented in Sprint 9: ${net}`, net, "node");
}
function lookupComponentProperty(id, property, ctx) {
    if (property !== "I") {
        throw new Error(`unsupported property ${property} on component ${id}`);
    }
    if (ctx.currentAnalysis === "DC") {
        const c = ctx.snapshot.dc?.sourceCurrents.get(id);
        if (c === undefined) {
            throw new ResolutionError(`component not found: ${id}`, id, "component");
        }
        return c;
    }
    if (ctx.currentAnalysis === "TRANSIENT") {
        const ts = ctx.snapshot.transient?.timeSteps;
        if (!ts || ts.length === 0) {
            throw new ResolutionError(`component not found: ${id}`, id, "component");
        }
        const last = ts[ts.length - 1];
        const v = last?.sourceCurrents.get(id);
        if (v === undefined) {
            throw new ResolutionError(`component not found: ${id}`, id, "component");
        }
        return v;
    }
    throw new ResolutionError(`AC component lookup not implemented in Sprint 9: ${id}`, id, "component");
}
function resolveFunction(name, args, ctx) {
    if (!isBuiltinName(name)) {
        const suggestion = suggestClosest(name, BUILTIN_NAMES);
        throw new Error(`unknown builtin '${name}'${suggestion !== null ? ` (did you mean ${suggestion}?)` : ""}`);
    }
    if (args.length !== 1 || !args[0]) {
        throw new Error(`${name}() expects exactly one argument`);
    }
    const arg = args[0];
    const userTarget = extractTargetName(arg, name);
    const isCurrent = name === "Ipeak" || name === "Irms";
    // Only voltage builtins get name→id mapping; current builtins
    // are keyed by component id (which the user already writes).
    const target = isCurrent ? userTarget : resolveNetKey(userTarget, ctx);
    if (ctx.currentAnalysis === "TRANSIENT" && ctx.snapshot.transient) {
        try {
            return {
                value: computeBuiltinTransient(name, target, ctx.snapshot.transient),
                unit: isCurrent ? "A" : "V",
            };
        }
        catch (err) {
            if (err instanceof RangeError) {
                throw new ResolutionError(err.message, userTarget, isCurrent ? "component" : "node");
            }
            throw err;
        }
    }
    if (ctx.currentAnalysis === "DC" && ctx.snapshot.dc) {
        try {
            return {
                value: computeBuiltinDC(name, target, ctx.snapshot.dc),
                unit: isCurrent ? "A" : "V",
            };
        }
        catch (err) {
            if (err instanceof RangeError) {
                throw new ResolutionError(err.message, userTarget, isCurrent ? "component" : "node");
            }
            throw err;
        }
    }
    throw new Error(`${name}() has no data for analysis ${ctx.currentAnalysis}`);
}
function extractTargetName(expr, fn) {
    if (expr.kind === "nodeRef")
        return expr.net;
    if (expr.kind === "componentRef")
        return expr.id;
    throw new Error(`${fn}() argument must be a node or component reference`);
}
// -------------------------------------------------------------
// Evaluators per AST variant
// -------------------------------------------------------------
function evaluateBinary(ast, snapshot, currentAnalysis, context) {
    const ctx = {
        snapshot,
        currentAnalysis,
        ...(context.nodeNameToId !== undefined ? { nodeNameToId: context.nodeNameToId } : {}),
    };
    const l = resolveExpr(ast.left, ctx);
    const r = resolveExpr(ast.right, ctx);
    const passed = applyComparator(ast.op, l.value, r.value);
    const margin = l.value - r.value;
    const unitBaseStr = unitBase(l.unit ?? r.unit);
    const actual = formatEngineering(l.value, unitBaseStr);
    const expected = `${ast.op} ${formatEngineering(r.value, unitBaseStr)}`;
    const exprText = exprToText(ast.left);
    const message = passed
        ? renderPassed(context.locale, exprText, actual)
        : renderFailedBinary(context.locale, exprText, actual, ast.op, formatEngineering(r.value, unitBaseStr));
    return {
        assertionId: context.assertionId,
        sourceText: context.sourceText,
        passed,
        skipped: false,
        message,
        actual,
        expected,
        margin: Math.abs(margin),
        marginUnit: unitBaseStr,
        analysisKind: currentAnalysis,
    };
}
function evaluateTolerance(ast, snapshot, currentAnalysis, context) {
    const ctx = {
        snapshot,
        currentAnalysis,
        ...(context.nodeNameToId !== undefined ? { nodeNameToId: context.nodeNameToId } : {}),
    };
    const l = resolveExpr(ast.expr, ctx);
    const absTol = ast.toleranceKind === "pct" ? Math.abs(ast.center) * ast.tolerance : ast.tolerance;
    const lower = ast.center - absTol;
    const upper = ast.center + absTol;
    const passed = l.value >= lower && l.value <= upper;
    const margin = Math.abs(l.value - ast.center);
    const unitBaseStr = unitBase(l.unit ?? ast.centerUnit);
    const exprText = exprToText(ast.expr);
    const actual = formatEngineering(l.value, unitBaseStr);
    const centerStr = formatEngineering(ast.center, unitBaseStr);
    const toleranceStr = ast.toleranceKind === "pct"
        ? `${(ast.tolerance * 100).toPrecision(3)}%`
        : formatEngineering(absTol, unitBaseStr);
    const lowerStr = formatEngineering(lower, unitBaseStr);
    const upperStr = formatEngineering(upper, unitBaseStr);
    const message = passed
        ? renderPassed(context.locale, exprText, actual)
        : renderFailedTolerance(context.locale, exprText, actual, centerStr, toleranceStr, lowerStr, upperStr);
    return {
        assertionId: context.assertionId,
        sourceText: context.sourceText,
        passed,
        skipped: false,
        message,
        actual,
        expected: `${centerStr} ±${toleranceStr}`,
        margin,
        marginUnit: unitBaseStr,
        analysisKind: currentAnalysis,
    };
}
// TODO(sprint-13, D2): replace this placeholder with a real FFT
// oscillation check. Plan:
//   1. Sprint 12 lands the AC sweep + complex-magnitude TimePoints.
//   2. Sprint 13 wires a Cooley-Tukey radix-2 FFT
//      (`packages/math/src/fft.ts`, ≤256 sample window) and a
//      peak-detector that finds the dominant frequency bin.
//   3. The evaluator compares the dominant bin against `ast.freq ±
//      ast.tolerance` and the source-attribution against
//      `ast.source` (default: any source counts).
//   4. Reference fixture: `tests/circuits/colpitts-oscillator.test.ts`
//      using a known 100 kHz LC tank, ±1% tolerance.
// Until then this evaluator returns `skipped: true` with the i18n
// "Sprint 13+" message. See SPRINT_09_REVIEW.md (D2) for context.
function evaluateOscillationPlaceholder(ast, currentAnalysis, context) {
    const exprText = exprToText(ast.expr);
    const freqStr = formatEngineering(ast.freq, "Hz");
    const message = renderOscillationPlaceholder(context.locale, exprText, freqStr);
    return {
        assertionId: context.assertionId,
        sourceText: context.sourceText,
        passed: false,
        skipped: true,
        message,
        actual: "not-checked",
        expected: `${freqStr} ±${formatEngineering(ast.tolerance, "Hz")}`,
        margin: NaN,
        marginUnit: "Hz",
        analysisKind: currentAnalysis,
    };
}
// TODO(sprint-10+, D4): the parser currently lowers
// `Ipeak(D1) < 100mA` to a `binary` AST with a `function` LHS, so
// this `evaluatePeak` branch is unreachable from `parseAssertion`.
// Reachable only via direct AST construction in unit tests. Choose
// one of:
//   (a) Sprint 10+ teaches the parser to emit `peak` for a sugar
//       form (e.g. `peak V(out) < 1V` keyword), then this evaluator
//       starts seeing real input. Carry-over to ast.ts: keep the
//       `peak` variant but document its invariants.
//   (b) Drop the `peak` variant entirely: remove from ast.ts +
//       evaluator switch + test fixtures. Keeps the AST minimal.
// Sprint 9 review concluded "(a) — defer scope to Sprint 10+".
// See SPRINT_09_REVIEW.md (D4) for the full rationale.
function evaluatePeak(ast, snapshot, currentAnalysis, context) {
    // Sprint 9 produces `peak` AST only from a dedicated shorthand the
    // parser currently does not emit (we lower to `binary` + function
    // call on the left). The stub below keeps the surface ready for
    // Sprint 10+ when the UI adds a sugar form.
    const ctx = {
        snapshot,
        currentAnalysis,
        ...(context.nodeNameToId !== undefined ? { nodeNameToId: context.nodeNameToId } : {}),
    };
    const l = resolveExpr(ast.expr, ctx);
    const unitBaseStr = unitBase(l.unit ?? ast.boundUnit);
    const exprText = exprToText(ast.expr);
    const actual = formatEngineering(Math.abs(l.value), unitBaseStr);
    const passed = ast.op === "<" ? Math.abs(l.value) < ast.bound : Math.abs(l.value) > ast.bound;
    const boundStr = formatEngineering(ast.bound, unitBaseStr);
    const message = passed
        ? renderPassed(context.locale, exprText, actual)
        : renderFailedPeak(context.locale, exprText, actual, ast.op, boundStr);
    return {
        assertionId: context.assertionId,
        sourceText: context.sourceText,
        passed,
        skipped: false,
        message,
        actual,
        expected: `${ast.op} ${boundStr}`,
        margin: Math.abs(Math.abs(l.value) - ast.bound),
        marginUnit: unitBaseStr,
        analysisKind: currentAnalysis,
    };
}
// -------------------------------------------------------------
// Helpers
// -------------------------------------------------------------
function applyComparator(op, a, b) {
    switch (op) {
        case "<":
            return a < b;
        case ">":
            return a > b;
        case "≤":
            return a <= b;
        case "≥":
            return a >= b;
        case "==":
            return Math.abs(a - b) < 1e-12;
        case "!=":
            return Math.abs(a - b) >= 1e-12;
        case "≈":
            // `≈` without tolerance treated as exact equality at the
            // machine-epsilon scale — tolerance form is handled by the
            // `tolerance` AST branch instead.
            return Math.abs(a - b) < 1e-9;
    }
    return false;
}
function conditionMatches(condition, current) {
    switch (condition.kind) {
        case "at":
        case "when":
            return condition.analysis === current;
        case "allTimes":
            return current === "TRANSIENT" || current === "AC";
    }
}
function exprToText(expr) {
    switch (expr.kind) {
        case "nodeRef":
            return `V(${expr.net})`;
        case "componentRef":
            return `${expr.property}(${expr.id})`;
        case "literal":
            return `${String(expr.value)}${expr.unit ?? ""}`;
        case "binary":
            return `(${exprToText(expr.left)} ${expr.op} ${exprToText(expr.right)})`;
        case "function":
            return `${expr.name}(${expr.args.map(exprToText).join(", ")})`;
    }
}
function skippedResult(ast, currentAnalysis, context) {
    const condition = ast.condition;
    const expectedAnalysis = condition === undefined || condition.kind === "allTimes" ? "ALL" : condition.analysis;
    const message = renderSkipped(context.locale, expectedAnalysis, currentAnalysis);
    return {
        assertionId: context.assertionId,
        sourceText: context.sourceText,
        passed: false,
        skipped: true,
        message,
        actual: "n/a",
        expected: "n/a",
        margin: NaN,
        marginUnit: "",
        analysisKind: currentAnalysis,
    };
}
function resolutionErrorResult(ast, err, currentAnalysis, context) {
    const pool = err.kind === "node" ? (context.knownNodes ?? []) : (context.knownComponents ?? []);
    const suggestion = suggestClosest(err.target, pool);
    const suggestionText = suggestion ?? "";
    const message = err.kind === "node"
        ? renderNodeNotFound(context.locale, err.target, suggestionText)
        : renderComponentNotFound(context.locale, err.target, suggestionText);
    return {
        assertionId: context.assertionId,
        sourceText: context.sourceText,
        passed: false,
        skipped: false,
        message,
        actual: "resolution-error",
        expected: "resolvable reference",
        margin: NaN,
        marginUnit: "",
        analysisKind: currentAnalysis,
        resolutionError: err.message,
    };
    void ast;
}
// -------------------------------------------------------------
// i18n rendering (thin wrappers around the bundle strings)
// -------------------------------------------------------------
const EN = {
    passed: (expr, actual) => `${expr} = ${actual} ✓`,
    failedBinary: (expr, actual, op, target) => `${expr} = ${actual}, expected ${op} ${target}`,
    failedTolerance: (expr, actual, center, tol, low, up) => `${expr} = ${actual}, expected ${center} ±${tol} (range: ${low} to ${up})`,
    failedPeak: (expr, actual, op, target) => `${expr} peak = ${actual}, expected ${op} ${target}`,
    oscillationPlaceholder: (expr, freq) => `Oscillation check of ${expr} at ${freq} requires FFT support (Sprint 13+).`,
    skipped: (expected, current) => `Skipped: assertion targets ${expected}, current analysis is ${current}.`,
    nodeNotFound: (net, suggestion) => `Node '${net}' not found in circuit.${suggestion ? ` Did you mean '${suggestion}'?` : ""}`,
    componentNotFound: (id, suggestion) => `Component '${id}' not found in circuit.${suggestion ? ` Did you mean '${suggestion}'?` : ""}`,
};
const TR = {
    passed: (expr, actual) => `${expr} = ${actual} ✓`,
    failedBinary: (expr, actual, op, target) => `${expr} = ${actual}, beklenen ${op} ${target}`,
    failedTolerance: (expr, actual, center, tol, low, up) => `${expr} = ${actual}, beklenen ${center} ±${tol} (aralık: ${low} - ${up})`,
    failedPeak: (expr, actual, op, target) => `${expr} tepe değeri = ${actual}, beklenen ${op} ${target}`,
    oscillationPlaceholder: (expr, freq) => `'${expr}' için ${freq} salınım denetimi FFT desteği gerektiriyor (Sprint 13+).`,
    skipped: (expected, current) => `Atlandı: iddia koşulu ${expected} hedefliyor, mevcut analiz ${current}.`,
    nodeNotFound: (net, suggestion) => `'${net}' adlı düğüm devrede bulunamadı.${suggestion ? ` '${suggestion}' demek mi istediniz?` : ""}`,
    componentNotFound: (id, suggestion) => `'${id}' adlı bileşen devrede bulunamadı.${suggestion ? ` '${suggestion}' demek mi istediniz?` : ""}`,
};
function bundle(locale) {
    return locale === "tr" ? TR : EN;
}
function renderPassed(locale, expr, actual) {
    return bundle(locale).passed(expr, actual);
}
function renderFailedBinary(locale, expr, actual, op, target) {
    return bundle(locale).failedBinary(expr, actual, op, target);
}
function renderFailedTolerance(locale, expr, actual, center, tol, low, up) {
    return bundle(locale).failedTolerance(expr, actual, center, tol, low, up);
}
function renderFailedPeak(locale, expr, actual, op, target) {
    return bundle(locale).failedPeak(expr, actual, op, target);
}
function renderOscillationPlaceholder(locale, expr, freq) {
    return bundle(locale).oscillationPlaceholder(expr, freq);
}
function renderSkipped(locale, expected, current) {
    return bundle(locale).skipped(expected, current);
}
function renderNodeNotFound(locale, net, suggestion) {
    return bundle(locale).nodeNotFound(net, suggestion);
}
function renderComponentNotFound(locale, id, suggestion) {
    return bundle(locale).componentNotFound(id, suggestion);
}
//# sourceMappingURL=evaluate.js.map